import {DestroyRef, Directive, inject, OnDestroy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {switchMap} from 'rxjs/operators';
import {
  DOWNLOAD_HISTORY_CREATED_EVENT,
  DOWNLOAD_HISTORY_STATUS,
  DOWNLOAD_HISTORY_UPDATED_EVENT,
  DownloadHistoryBasePayload,
  DownloadHistoryUpdatedPayload
} from '@services/download-history/download-history.models';
import {DownloadHistoryService} from '@services/download-history/download-history.service';
import {toast} from 'ngx-sonner';
import {ProgressNotificationComponent} from '@shared/components/progress-notification/progress-notification.component';
import {RealtimeChannelService, UserContextService} from '@pages/home/pusher-listener/pusher-listener.service';

function openToast({operationId: id, filename, statusAlias, progress, downloadUrl}: Partial<DownloadHistoryUpdatedPayload>) {
  toast.custom(ProgressNotificationComponent, {
    id,
    duration: Infinity,
    dismissible: false,
    position: 'bottom-right',
    componentProps: {
      requests: {
        id,
        filename,
        statusAlias,
        progress,
        downloadUrl
      }
    }
  });
}

@Directive({
  selector: '[octDownloadHistoryNotifications]'
})
export class DownloadHistoryNotifications implements OnDestroy {
  private readonly _userContext = inject(UserContextService);
  private readonly _realtime = inject(RealtimeChannelService);
  private readonly _downloadHistoryService = inject(DownloadHistoryService);
  private readonly _destroyRef = inject(DestroyRef);

  constructor() {
    this._listenToUpdated();
    this._listenToCreated();
  }

  private _listenToUpdated() {
    this._userContext.userContext
      .pipe(
        switchMap(channelName => this._realtime.listen<DownloadHistoryUpdatedPayload>(channelName, DOWNLOAD_HISTORY_UPDATED_EVENT)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(data => this._getDownloadHistoryByOperationId(data.operationId, data?.filename ?? '', true));
  }

  private _listenToCreated() {
    this._userContext.userContext
      .pipe(
        switchMap(channelName => this._realtime.listen<DownloadHistoryBasePayload>(channelName, DOWNLOAD_HISTORY_CREATED_EVENT)),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(data => this._getDownloadHistoryByOperationId(data.operationId, data?.filename ?? ''));
  }

  private _getDownloadHistoryByOperationId(operationId: string, filename: string, shouldDismiss = false) {
    if (!operationId) {
      return;
    }

    this._downloadHistoryService
      .getDownloadHistoryByOperationId(operationId)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(history => {
        if (!history?.filename) {
          history.filename = filename;
        }

        if (shouldDismiss) {
          toast.dismiss(operationId);
        }

        this._openToast(history);
      });
  }

  private _openToast(data: DownloadHistoryUpdatedPayload) {
    openToast({
      operationId: data.operationId,
      filename: data?.filename ?? `download-${data.operationId}`,
      statusAlias: data?.statusAlias ?? DOWNLOAD_HISTORY_STATUS.STARTING,
      progress: data?.progress ?? 0,
      downloadUrl: data.downloadUrl
    });
  }

  ngOnDestroy() {
    this._realtime.unsubscribe();
  }
}
